import React, {useEffect, useRef, useState} from "react";
import * as Yup from "yup";
import {FormikProps, useFormik} from "formik";
import Countdown from "react-countdown";
import {addMinutes} from "date-fns";

import {Forms, GradientButton} from "@housebets/housebets-x-package";
import {CodeInput} from '@housebets/housebets-x-package/lib/components/form/CodeInput';

import {IPinFormProps, TFormikConfig, TPayload} from "./types";
import {useIntl} from "react-intl";

const initData: TFormikConfig = {
    pin: "",
};

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({...initData});
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};

export const PinForm: React.FC<IPinFormProps> = ({
    isLoading,
    requestCodeButtonText,
    submitButtonText,
    title,
    value,
    onRequestCode,
    onSubmit,
    timeout = 0.5,
    error,
    type = "pin",
    modifyButtonText,
    onModify,
}) => {
    const {$t} = useIntl();
    const [timeEnd, setTimeEnd] = useState(false);
    const [date, setDate] = useState<Date>(addMinutes(new Date(), timeout));
    const [errorMsg, setErrorMsg] = useState(error);
    const ref = useRef<Countdown | null>(null);

    const formSchema = () =>
        Yup.object().shape({
            pin: Yup.string()
                .required($t({id: "FieldIsRequired"}))
                .length(6),
        });

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {...initData},
        validationSchema: formSchema(),
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async (data) => {
            const payload: TPayload = {
                pin: data.pin,
                value,
            };
            onSubmit(payload);
        },
    });

    useEffect(() => {
        setErrorMsg(error);
    }, [error]);

    useEffect(() => {
        initForm(formik);
    }, []);

    return (
        <Forms.SecurityVerify.Wrapper>
            <Forms.SecurityVerify.Content>
                <Forms.SecurityVerify.Title>{title}</Forms.SecurityVerify.Title>

                <Forms.SecurityVerify.Value>{value}</Forms.SecurityVerify.Value>

                {modifyButtonText && <Forms.SecurityVerify.EditValue onClick={onModify}>{modifyButtonText}</Forms.SecurityVerify.EditValue>}
            </Forms.SecurityVerify.Content>

            {type === "pin" && (
                <Forms.SecurityVerify.Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSubmit(formik.values);
                    }}>
                    <Forms.SecurityVerify.Row error={errorMsg}>
                        <CodeInput
                            label=""
                            name="pin"
                            inputMode="numeric"
                            type="text"
                            disabled={false}
                            fields={6}
                            value={formik.values.pin}
                            error={
                                formik?.errors?.pin
                                    ? {
                                          message: "",
                                      }
                                    : null
                            }
                            touch={() =>
                                formik.setTouched({
                                    pin: true,
                                })
                            }
                            untouch={() =>
                                formik.setTouched({
                                    pin: false,
                                })
                            }
                            onChange={(v) => {
                                formik.setValues({
                                    pin: v,
                                });

                                formik.setTouched({
                                    pin: true,
                                });
                            }}
                        />
                    </Forms.SecurityVerify.Row>
                    <Forms.SecurityVerify.Row>
                        <GradientButton text={submitButtonText} type="submit" kind="fill" disabled={!formik.isValid || isLoading} />
                    </Forms.SecurityVerify.Row>
                </Forms.SecurityVerify.Form>
            )}

            {type === "no-pin" && (
                <Forms.SecurityVerify.Row>
                    <GradientButton
                        text={$t({id: "Dismiss"})}
                        kind="fill"
                        disabled={!formik.isValid || isLoading}
                        onClick={() => {
                            onSubmit();
                        }}
                    />
                </Forms.SecurityVerify.Row>
            )}

            <Forms.SecurityVerify.Content>
                <Countdown
                    ref={ref}
                    date={date}
                    renderer={({minutes, seconds, total}) => {
                        if (total === 0) {
                            setTimeEnd(true);
                        } else {
                            setTimeEnd(false);
                        }

                        return (
                            <div>
                                {minutes < 10 ? `0${minutes}` : minutes}: {seconds < 10 ? `0${seconds}` : seconds}
                            </div>
                        );
                    }}
                />

                <Forms.SecurityVerify.RequestCode
                    disabled={!timeEnd}
                    onClick={() => {
                        onRequestCode();
                        setDate(addMinutes(new Date(), timeout));
                    }}>
                    {requestCodeButtonText}{' '}
                    {timeEnd ? `(${timeEnd})` : ''}
                </Forms.SecurityVerify.RequestCode>
            </Forms.SecurityVerify.Content>
        </Forms.SecurityVerify.Wrapper>
    );
};

PinForm.displayName = "PinForm";
